import type {
  AgentConfig,
  ChannelSettings,
  AgentStreamData,
  AgentReminderRules,
  JobMatchAttribute,
} from 'src/types/agent-builder';
import type {Dispatch, ThunkAction} from 'src/reducers';

import * as reduxApi from 'src/utils/redux-api-v2';


export const RECEIVE_CHANNEL_SETTINGS =
  'agent-builder/receive_channel_settings';
export const RECEIVE_AGENT_SUGGESTION =
  'agent-builder/receive_agent_suggestion';
export const RECEIVE_AGENT_STREAM_DATA =
  'agent-builder/receive_agent_stream_data';
export const RECEIVE_AGENT_CONFIGURATION =
  'agent-builder/receive_agent_configuration';
export const RECEIVE_AGENT_BUILDING = 'agent-builder/receive_agent_building';
export const RECEIVE_AGENT_REMINDER = 'agent-builder/receive_agent_reminder';
export const REQUEST_AGENT_ADDITIONAL_MODULES =
  'agent-builder/request_agent_additional_modules';
export const RECEIVE_AGENT_ADDITIONAL_MODULES =
  'agent-builder/receive_agent_additional_modules';
export const REQUEST_AGENT_JOB_MATCH_ATTRIBUTES =
  'agent-builder/request_agent_job_match_attributes';
export const RECEIVE_AGENT_JOB_MATCH_ATTRIBUTES =
  'agent-builder/receive_agent_job_match_attributes';

export interface ReceiveChannelSettingsAction {
  type: typeof RECEIVE_CHANNEL_SETTINGS;
  payload: ChannelSettings;
}

export interface ReceiveAgentSuggestionAction {
  type: typeof RECEIVE_AGENT_SUGGESTION;
  payload: string | null;
}

export interface ReceiveAgentStreamDataAction {
  type: typeof RECEIVE_AGENT_STREAM_DATA;
  payload: Partial<AgentStreamData>;
}

export interface ReceiveAgentConfigurationAction {
  type: typeof RECEIVE_AGENT_CONFIGURATION;
  payload: AgentConfig | null;
}

export interface ReceiveAgentBuildingAction {
  type: typeof RECEIVE_AGENT_BUILDING;
  payload: boolean;
}

export interface ReceiveAgentReminderAction {
  type: typeof RECEIVE_AGENT_REMINDER;
  payload: AgentReminderRules;
}

export interface RequestAgentAdditionalModulesAction {
  type: typeof REQUEST_AGENT_ADDITIONAL_MODULES;
  payload: string;
}

export interface ReceiveAgentAdditionalModulesAction {
  type: typeof RECEIVE_AGENT_ADDITIONAL_MODULES;
  payload: {agentId: string; modules: string[]};
}

export interface RequestAgentJobMatchAttributesAction {
  type: typeof REQUEST_AGENT_JOB_MATCH_ATTRIBUTES;
  payload: string;
}

export interface ReceiveAgentJobMatchAttributesAction {
  type: typeof RECEIVE_AGENT_JOB_MATCH_ATTRIBUTES;
  payload: {agentId: string; attributes: JobMatchAttribute[]};
}

export type AgentBuilderActions =
  | ReceiveChannelSettingsAction
  | ReceiveAgentSuggestionAction
  | ReceiveAgentStreamDataAction
  | ReceiveAgentConfigurationAction
  | ReceiveAgentBuildingAction
  | ReceiveAgentReminderAction
  | RequestAgentAdditionalModulesAction
  | ReceiveAgentAdditionalModulesAction
  | RequestAgentJobMatchAttributesAction
  | ReceiveAgentJobMatchAttributesAction;

export const receiveChannelSettings = (
  payload: ChannelSettings,
): ReceiveChannelSettingsAction => ({
  type: RECEIVE_CHANNEL_SETTINGS,
  payload,
});

export const receiveAgentSuggestion = (
  payload: string | null,
): ReceiveAgentSuggestionAction => ({
  type: RECEIVE_AGENT_SUGGESTION,
  payload,
});

export const receiveAgentConfiguration = (
  payload: AgentConfig,
): ReceiveAgentConfigurationAction => ({
  type: RECEIVE_AGENT_CONFIGURATION,
  payload,
});

export const receiveAgentStreamData = (
  payload: Partial<AgentStreamData>,
): ReceiveAgentStreamDataAction => ({
  type: RECEIVE_AGENT_STREAM_DATA,
  payload,
});

export const receiveAgentBuilding = (
  payload: boolean,
): ReceiveAgentBuildingAction => ({
  type: RECEIVE_AGENT_BUILDING,
  payload,
});

export const receiveAgentReminder = (
  payload: AgentReminderRules,
): ReceiveAgentReminderAction => ({
  type: RECEIVE_AGENT_REMINDER,
  payload,
});

export const requestAgentAdditionalModules = (
  agentId: string,
): RequestAgentAdditionalModulesAction => ({
  type: REQUEST_AGENT_ADDITIONAL_MODULES,
  payload: agentId,
});

export const receiveAgentAdditionalModules = (
  agentId: string,
  modules: string[],
): ReceiveAgentAdditionalModulesAction => ({
  type: RECEIVE_AGENT_ADDITIONAL_MODULES,
  payload: {agentId, modules},
});

export const getAdditionalModules =
  (agentId: string): ThunkAction<string[]> =>
  async (dispatch: Dispatch): Promise<string[]> => {
    dispatch(requestAgentAdditionalModules(agentId));
    try {
      const response = await dispatch<string[]>(
        reduxApi.get(`agent-builder/agents/${agentId}/additional-modules`),
      );
      const modules = Array.isArray(response) ? response : [];
      dispatch(receiveAgentAdditionalModules(agentId, modules));
      return modules;
    } catch (error) {
      dispatch(receiveAgentAdditionalModules(agentId, []));
      throw error;
    }
  };

export const requestAgentJobMatchAttributes = (
  agentId: string,
): RequestAgentJobMatchAttributesAction => ({
  type: REQUEST_AGENT_JOB_MATCH_ATTRIBUTES,
  payload: agentId,
});

export const receiveAgentJobMatchAttributes = (
  agentId: string,
  attributes: JobMatchAttribute[],
): ReceiveAgentJobMatchAttributesAction => ({
  type: RECEIVE_AGENT_JOB_MATCH_ATTRIBUTES,
  payload: {agentId, attributes},
});

export const getJobMatchAttributes =
  (agentId: string): ThunkAction<JobMatchAttribute[]> =>
  async (dispatch: Dispatch): Promise<JobMatchAttribute[]> => {
    dispatch(requestAgentJobMatchAttributes(agentId));
    try {
      const response = await dispatch<JobMatchAttribute[]>(
        reduxApi.get(`agent-builder/agents/${agentId}/job-match-attributes`),
      );
      const attributes = Array.isArray(response) ? response : [];
      dispatch(receiveAgentJobMatchAttributes(agentId, attributes));
      return attributes;
    } catch (error) {
      dispatch(receiveAgentJobMatchAttributes(agentId, []));
      throw error;
    }
  };

export const getChannelSettings =
  (agentID: string): ThunkAction<ChannelSettings> =>
  async (dispatch: Dispatch): Promise<ChannelSettings> => {
    try {
      const response = await dispatch<ChannelSettings>(
        reduxApi.get(`agent-builder/agents/${agentID}/channel_settings`),
      );
      return dispatch(receiveChannelSettings(response));
    } catch (error: any) {
      console.error(error);
      throw new Error('Channel settings error.');
    }
  };

export const getAgentConfiguration =
  (agentID: string): ThunkAction<AgentConfig> =>
  async (dispatch: Dispatch): Promise<AgentConfig> => {
    try {
      const response = await dispatch<AgentConfig>(
        reduxApi.get(`agent-builder/agents/${agentID}/config`),
      );
      return dispatch(receiveAgentConfiguration(response));
    } catch (error: any) {
      console.error(error);
      throw new Error('Agent configuration error.');
    }
  };

const mockDatabase: AgentReminderRules = {
  SMS: {
    never_started: {
      enabled: true,
      interval: {
        frequency: 1,
        unit: 'hours',
      },
      occurrences: 1,
      message_rte: {
        blocks: [],
        entityMap: {},
      },
    },
    abandoned: {
      enabled: false,
      interval: {
        frequency: 1,
        unit: 'days',
      },
      occurrences: 1,
      message_rte: {
        blocks: [],
        entityMap: {},
      },
    },
  },
  EMAIL: {
    never_started: {
      enabled: true,
      interval: {
        frequency: 1,
        unit: 'hours',
      },
      occurrences: 1,
      type: 'basic',
      basic: {
        subject: '',
        body_rte: {
          blocks: [],
          entityMap: {},
        },
        beefree_json: null,
      },
      advanced: {
        subject: '',
        body_rte: {
          blocks: [],
          entityMap: {},
        },
        beefree_json: null,
      },
    },
    abandoned: {
      enabled: false,
      interval: {
        frequency: 1,
        unit: 'days',
      },
      occurrences: 1,
      type: 'basic',
      basic: {
        subject: '',
        body_rte: {
          blocks: [],
          entityMap: {},
        },
        beefree_json: null,
      },
      advanced: {
        subject: '',
        body_rte: {
          blocks: [],
          entityMap: {},
        },
        beefree_json: null,
      },
    },
  },
};

export const getAgentReminder =
  (agentID: string): ThunkAction<AgentReminderRules> =>
  async (dispatch: Dispatch): Promise<AgentReminderRules> => {
    try {
      const response = await dispatch<AgentReminderRules>(
        reduxApi.get(`agent-builder/agents/${agentID}/reminder`),
      );
      // const response = mockDatabase;

      return dispatch(receiveAgentReminder(response));
    } catch (error: any) {
      console.error(error);
      // throw new Error('Agent reminder error.');
      // Just for testing purposes
      return dispatch(receiveAgentReminder(mockDatabase));
    }
  };
